import { useTheme } from '@mui/material';
import { formatCurrency, normalizeAddDigits } from 'app/utils/normalizer';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { Heading4, TextM } from 'app/components';
import {
  ReviewSimulationBox,
  ReviewSimulationPreviewItem,
  ReviewSimulationPreviewItems,
  ReviewStepBox,
} from '../../styles';
import { ReviewStepSimulationDivider } from '../ReviewStepSimulationDivider';

interface ReviewStepSimulationV2Props {
  simulationV2: {
    num_periods?: number;
    payment_amount?: number;
    requested_amount?: number;
    disbursement_amount?: number;
    financed_amount?: number;
    interest_rate?: number;
    monthly_effective_interest_rate?: number;
    tac_amount?: number;
    iof_amount?: number;
    insurance_amount?: number;
    first_payment_date?: string;
    last_payment_date?: string;
    total_amount_owed?: number;
  } | null;
}

const ReviewStepSimulationV2 = ({
  simulationV2,
}: ReviewStepSimulationV2Props) => {
  const theme = useTheme();

  if (!simulationV2) {
    return null;
  }

  return (
    <ReviewStepBox marginTop="0px" marginBottom="16px">
      <Heading4
        style={{
          color: theme.palette.brand.secondary.base,
        }}
      >
        Valores da solicitação:
      </Heading4>
      <ReviewSimulationBox>
        <ReviewSimulationPreviewItems>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Quant. parcelas
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.num_periods && simulationV2?.num_periods > 0
                  ? simulationV2?.num_periods
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor da parcela
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.payment_amount
                  ? formatCurrency(simulationV2?.payment_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor solicitado
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.requested_amount
                  ? formatCurrency(simulationV2?.requested_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor liberado
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.disbursement_amount
                  ? formatCurrency(simulationV2?.disbursement_amount || 0)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor financiado
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.financed_amount
                  ? formatCurrency(simulationV2?.financed_amount || 0)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Juros
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.interest_rate
                  ? `${normalizeAddDigits(
                      simulationV2?.interest_rate * 100,
                      2,
                    )?.replace('.', ',')}% a.m.`
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                CET
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {typeof simulationV2?.monthly_effective_interest_rate ===
                'number'
                  ? `${normalizeAddDigits(
                      simulationV2?.monthly_effective_interest_rate * 100,
                      2,
                    ).replace('.', ',')}% a.m.`
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
        </ReviewSimulationPreviewItems>
        <ReviewSimulationPreviewItems>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                TC
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.tac_amount
                  ? formatCurrency(simulationV2?.tac_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                IOF
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.iof_amount
                  ? formatCurrency(simulationV2?.iof_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Valor do Seguro
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.insurance_amount
                  ? formatCurrency(simulationV2?.insurance_amount)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Primeiro vencimento
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.first_payment_date
                  ? formatDateFromBackend(
                      simulationV2?.first_payment_date,
                    ).toLocaleDateString('pt-br')
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Último vencimento
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.last_payment_date
                  ? formatDateFromBackend(
                      simulationV2?.last_payment_date,
                    ).toLocaleDateString('pt-br')
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
          <>
            <ReviewSimulationPreviewItem>
              <TextM
                weight="Semibold"
                style={{ color: theme.palette.brand.secondary.base }}
              >
                Saldo Total
              </TextM>
              <TextM style={{ color: theme.palette.brand.secondary.base }}>
                {simulationV2?.total_amount_owed
                  ? formatCurrency(simulationV2?.total_amount_owed)
                  : '-'}
              </TextM>
            </ReviewSimulationPreviewItem>
            <ReviewStepSimulationDivider theme={theme} />
          </>
        </ReviewSimulationPreviewItems>
      </ReviewSimulationBox>
    </ReviewStepBox>
  );
};

export default ReviewStepSimulationV2;
